import cn from 'classnames';
import { forwardRef } from 'react';

import { CheckSmallIcon } from '../../foundations';
import { Checkbox } from '../../inputs/checkbox';

import type { ForwardedRef, ReactElement } from 'react';

import type { ListboxItemProps } from './listbox-item.types';

import './listbox-item.scss';

/**
 * @internal
 * The `ListboxItem` component is used to define the items of the `Listbox` component.
 * It mostly should be used as internal component to bring defined styles to the list items in menu
 * or select components.
 *
 * ### Usage
 *
 * ```tsx
 * import { Listbox, ListboxItem } from '@bloomreach/react-banana-ui';
 *
 * export default function MyMenu() {
 *   return (
 *      <Listbox>
 *        <ListboxItem>Item 1</ListboxItem>
 *        <ListboxItem>Item 2</ListboxItem>
 *        <ListboxItem>Item 3</ListboxItem>
 *      </Listbox>
 *   );
 * }
 * ```
 */
const ListboxItem = forwardRef(
  (props: ListboxItemProps, forwardedRef: ForwardedRef<HTMLLIElement> | null): ReactElement => {
    const {
      checkType,
      children,
      className,
      disabled,
      highlighted,
      role = 'option',
      selected,
      startIcon,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const classes = cn(
      'rbui-listbox-item',
      {
        'rbui-listbox-item--disabled': disabled,
        'rbui-listbox-item--highlighted': highlighted,
        'rbui-listbox-item--selected': selected,
      },
      className,
    );

    return (
      <li className={classes} ref={forwardedRef} role={role} {...rest}>
        {checkType === 'checkbox' && (
          <Checkbox checked={selected} className="rbui-listbox-item__checkbox" disabled={disabled} />
        )}
        {startIcon && <span className="rbui-listbox-item__start-icon">{startIcon}</span>}
        {children}
        {checkType === 'checkmark' && <CheckSmallIcon className="rbui-listbox-item__check-mark" />}
      </li>
    );
  },
);

ListboxItem.displayName = 'ListboxItem';

export default ListboxItem;
